import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
	Slider,
	Button,
	MultiSelect,
	DateRangePicker, MultiSelectOption, Loader
} from '@kaleyra/emmy';
import Styles from './Logs.module.scss';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import moment from 'moment';
import { isEmpty } from "lodash";
import { consolidatedLogsFilterType } from "../constants";

export type MapStateToPropsType = {
	params: any;
	flowLogsFilterFlowNames: any,
	flowLogsFilterInProgress: boolean | undefined,
};

export type MapDispatchToPropsType = {
	closeFilterModal: () => void;
	applyFilters: (filters: any) => void;
	loadFlowLogsFilterFlowNames: (filter: any) => void,
};

const FilterModal: React.FC<MapStateToPropsType & MapDispatchToPropsType> = (
	{
		closeFilterModal,
		params,
		applyFilters,
		loadFlowLogsFilterFlowNames,
		flowLogsFilterFlowNames,
		flowLogsFilterInProgress
	}) => {
	const [flowName, setFlowName] = useState(undefined);
	const [triggerType, setTriggerType] = useState(undefined);
	const [status, setStatus] = useState(undefined);
	const [date, setDate] = useState<any>(null);
	const { dateTag, setDateTag, statusTag, setStatusTag, triggerTag, setTriggerTag, flowNameTag, setFlowNameTag, filterDropdowns } = params;
	const [defaultFlowName] = useState<any>(flowNameTag?.length ? flowNameTag : undefined);
	const { t } = useTranslation();
	useEffect(() => {
		if (dateTag) setDate(dateTag);
		if (statusTag) setStatus(statusTag);
		if (triggerTag) setTriggerType(triggerTag);
		if (flowNameTag) setFlowName(flowNameTag);
	}, []);
	const disabledDate = (current: any) => {
		// Can not select days after today
		return current && current > moment().endOf('day');
	};
	const selectChangeHandler = useCallback((val) => {
		setStatus(val);
	}, []);

	const handleSave = useCallback(() => {

		let filters: consolidatedLogsFilterType = {};
		flowName ? filters.flow_name = flowName : delete filters.flow_name;
		!isEmpty(triggerType) ? filters.trigger = triggerType : delete filters.trigger;
		!isEmpty(status) ? filters.status = status : delete filters.status;
		if (!isEmpty(date) && date.length > 1 && date[0] && date[1]) {
			filters.from_flow_start_date = moment(date[0]).format('YYYY-MM-DD');
			filters.to_flow_start_date = moment(date[1]).format('YYYY-MM-DD');
			setDateTag(date);
		} else {
			delete filters.from_flow_start_date;
			delete filters.to_flow_start_date;
			setDateTag(null);
		}
		applyFilters(filters);

		// set the tags
		setStatusTag(filters.status);
		setTriggerTag(filters.trigger);
		setFlowNameTag(filters.flow_name);

		closeFilterModal();
	}, [status, triggerType, flowName, date]);

	const enableSaveButton = useMemo(() => {
		return status || date || flowName || triggerType;
	}, [status, date, flowName, triggerType]);

	const renderFooter = () => {
		return (
			<div className={Styles.footer}>
				<span className={Styles.cancelBtn}>
					<Button type="tertiary" onClick={() => closeFilterModal()}>
						{t('mainCancel')}
					</Button>
				</span>
				<Button
					disabled={!enableSaveButton}
					type="primary"
					onClick={handleSave}
				>
					{t('BLD01036')}
				</Button>
			</div>
		);
	};
	const setValue = () => {
		if (dateTag && dateTag.length) {
			return dateTag
		}
		else {
			return undefined
		}
	}
	useEffect(() => {
		loadFlowLogsFilterFlowNames({});
	}, [])

	return (
		<Slider
			title={t('mainFilter')}
			containerId="root-modal"
			onClose={() => closeFilterModal()}
			visibility={true}
			footer={renderFooter()}
		>
			{ (flowLogsFilterInProgress === undefined || flowLogsFilterInProgress) && <Loader />}
			{ flowLogsFilterInProgress === false && <div className={cn(Styles.filterModal, 'flowBuilderLogsFilterModal')}>
				<MultiSelect
					value={flowName}
					selectedValues={flowName}
					onChange={(val: any) => {
						if (val?.length) {
							const result = val.filter((ele: any) => ele.trim().length > 0);
							if (result?.length) {
								setFlowName(result);
							}
						}
						else {
							setFlowName(val);
						}
					}}
					mode="multiple"
					defaultValue={defaultFlowName}
					placeholder={t('KLR00435')}
					className={cn(Styles.mrgBtm)}
					maxTagCount={6}
				>
					{!isEmpty(flowLogsFilterFlowNames) &&
						flowLogsFilterFlowNames.map(
							(option: string) => {
								return (
									<MultiSelectOption
										key={option}
										value={option}
									>
										{option}
									</MultiSelectOption>
								);
							}
						)}
				</MultiSelect>
				<MultiSelect
					placeholder={t('KLR00424')}
					selectedValues={triggerType}
					value={triggerType}
					onChange={(val: any) => setTriggerType(val)}
					defaultValue={triggerTag}
					className={cn(Styles.mrgBtm)}
					mode="multiple"
				>
					{Object.entries((filterDropdowns || {}).triggers || {}).map(([key, value]:any) => (
						<MultiSelectOption
							key={key}
							value={key}
						>
							{value}
						</MultiSelectOption>
					))}
				</MultiSelect>
				<MultiSelect
					placeholder={t('statusPlaceholder')}
					selectedValues={status}
					value={status}
					defaultValue={statusTag}
					onChange={(event: any) => selectChangeHandler(event)}
					className={cn(Styles.mrgBtm)}
					mode="multiple"
				>
					{Object.entries((filterDropdowns || {}).status || {}).map(([key, value]:any) => (
						<MultiSelectOption
							key={key}
							value={key}
						>
							{value}
						</MultiSelectOption>
					))}
				</MultiSelect>
				<div>
					<div className={Styles.compSubHeader}>
						{t('KLR00426')}
					</div>
					<DateRangePicker
						onOk={(date: any) => setDate(date)}
						inputStyle={Styles.datePickerWrapper}
						value={setValue()}
						disabledDate={disabledDate}
						okBtnText={t('done')}
						cancelBtnText={t('mainCancel')}
						placeholder={[t('flwMailFrom'), t('mainTo')]}
						onClear={() => {
							setDate(null)
						}}
					/>
				</div>
			</div>}
		</Slider>
	);
};

export default FilterModal;
